// ============================================================================
// TRANSCRIPT — LifeOS School.dc.html, option 6c, the left column.
//
// A searchable, timestamped transcript. Segments carry start_ms/end_ms (the citation convention
// from blueprint/03), which is what lets anything in this app say "Lecture 5 · 12:31" instead of
// waving at a whole recording.
//
// WHO WAS SPEAKING, AND THE WORD "LIKELY". The desktop worker can separate the voices in a
// recording; it cannot identify anyone, because nothing here has a sample of any professor's
// voice to compare against. So one rule is applied — the voice that talks the most across the
// whole recording is most likely the lecturer — and this panel prints the rule and the share
// rather than the conclusion alone: "Professor (likely) spoke 88% · 1 other voice · decided by
// talk time". Everyone else is "Speaker 2", "Speaker 3": unnamed, because a different voice is
// the whole of what was established. When `transcript.speakers` is null NOTHING is drawn — no
// header, no "unknown speaker" on a line. Hidden ≠ missing; a label nobody computed is absent,
// not blank.
//
// THE AUDIO BANNER IS THE HONEST BIT. The raw audio is archived on the owner's own PC and the
// cloud copy is DELETED after transcription — that is the storage decision (CLAUDE.md →
// Architecture), not a limitation to hide. The desktop worker exists now and pushes transcripts
// up; nothing pulls a file back down, and no route is planned that would. So the banner says the
// recording is not here, and the playback affordances are visibly inert rather than silently
// dead. A play button that quietly does nothing is worse than one that admits what it cannot do.
// ============================================================================

import { useMemo, useState } from 'react'
import {
  msToTimestamp,
  type Transcript as TranscriptMeta,
  type TranscriptSegment,
  type TranscriptSpeaker,
} from '@app/shared'

import { SkeletonRows } from '../Card'
import { Search } from '../icons'
import { PlayCircle } from './icons'
import { Unavailable } from './Chrome'

export function TranscriptPanel({
  transcript,
  segments,
  isPending,
  error,
  initialQuery = '',
}: {
  transcript: TranscriptMeta | undefined
  segments: TranscriptSegment[] | undefined
  isPending: boolean
  error: unknown
  /** Seeded from `?q=` so a search result can deep-link straight to the matching moment. */
  initialQuery?: string
}) {
  const [query, setQuery] = useState(initialQuery)
  const [activeMatch, setActiveMatch] = useState(0)

  const rows = segments ?? []
  const needle = query.trim().toLowerCase()
  const matches = useMemo(
    () => (needle ? rows.filter((s) => s.text.toLowerCase().includes(needle)) : []),
    [rows, needle],
  )
  const activeId = matches.length > 0 ? matches[activeMatch % matches.length]?.id : undefined

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-[11px] overflow-hidden rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex flex-none items-center gap-[9px]">
        <h2 className="text-[13px] font-semibold leading-none">Transcript</h2>
        {transcript && (
          <span className="text-[11px] font-normal leading-none text-microcopy">
            {/* The transcriber is named when the row records one and OMITTED when it does not.
                Not 'unknown', not a default: an absent field is absent from the UI, and printing
                a model nobody recorded would be provenance the reader cannot check. */}
            {transcript.wordCount.toLocaleString('en-US')} words
            {transcript.model ? ` · ${transcript.model}` : ''}
          </span>
        )}

        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            // Enter walks to the next hit. There are no next/prev buttons in the mockup and this
            // is what the counter beside the field is for.
            setActiveMatch((n) => n + 1)
          }}
          className="mo-field ml-auto flex w-[210px] flex-none items-center gap-[9px] rounded-button border border-line-2 px-[11px] py-[7px]"
        >
          <Search size={13} strokeWidth={1.9} className="flex-none text-microcopy" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveMatch(0)
            }}
            placeholder="Search transcript"
            aria-label="Search this transcript"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[11.5px] font-medium leading-none text-ink-2 outline-none placeholder:text-microcopy"
          />
          {needle && (
            <span className="flex-none text-[10px] font-semibold leading-none text-microcopy">
              {/* 0/0 is said out loud rather than hidden: "no matches" is an answer. */}
              {matches.length === 0 ? '0/0' : `${(activeMatch % matches.length) + 1}/${matches.length}`}
            </span>
          )}
        </form>
      </header>

      <div className="flex flex-none items-center gap-2 rounded-button bg-surface-4 px-[11px] py-2">
        <PlayCircle size={12} className="flex-none text-muted" />
        <p className="flex-1 text-[11.5px] font-normal leading-[1.35] text-ink-3 text-pretty">
          The recording itself is archived on your desktop PC — the cloud copy is deleted after
          transcription, on purpose. Nothing here can play it back; the timestamps are for
          finding your place in the words.
        </p>
        <button
          type="button"
          disabled
          title="Nothing yet asks the desktop PC to hand a file back. The transcription worker only pushes transcripts up — there is no route in the other direction, so this button would have nowhere to send the request."
          className="flex-none cursor-not-allowed whitespace-nowrap text-[11px] font-semibold leading-none text-muted"
        >
          Request file
        </button>
      </div>

      {transcript?.speakers && transcript.speakers.length > 0 && (
        <SpeakerNote speakers={transcript.speakers} />
      )}

      {isPending && <SkeletonRows rows={8} />}
      <Unavailable error={error} />

      {segments && rows.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy text-pretty">
          This lecture is marked transcribed but no segments came back. Nothing was lost — the
          audio is still archived on your PC.
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto">
        {rows.map((segment, index) => (
          <SegmentRow
            key={segment.id}
            segment={segment}
            needle={needle}
            active={segment.id === activeId}
            // Named at each CHANGE of voice, not on every line: a lecture is one person for most
            // of its runtime and a label on all 300 rows would be noise you stop reading, which
            // is exactly when the one line that IS a student slips past.
            showSpeaker={segment.speaker !== null && segment.speaker !== (rows[index - 1]?.speaker ?? null)}
          />
        ))}
      </div>
    </section>
  )
}

/**
 * The stored label turned into words a reader can weigh.
 *
 * `professor` becomes "Professor (likely)" — the parenthesis is not decoration. The desktop
 * worker picked that voice because it talked the most, not because it recognised anyone, and a
 * bare "Professor" would be the app claiming knowledge it does not have. `speaker-2` becomes
 * "Speaker 2": a different voice, and nothing more is asserted.
 *
 * Anything else is printed exactly as stored. A label this function does not recognise came from
 * somewhere and inventing a friendlier name for it would be worse than showing it.
 */
function speakerLabel(speaker: string): string {
  if (speaker === 'professor') return 'Professor (likely)'
  const numbered = /^speaker-(\d+)$/.exec(speaker)
  return numbered ? `Speaker ${numbered[1]}` : speaker
}

/**
 * One line above the transcript: who was talking, how much, and what decided it.
 *
 * The rule is named ("decided by talk time") because the reader is the only one who knows
 * whether it holds — he was in the room. In a lecture it is nearly always right; in a discussion
 * section it can be flatly wrong, and he can see that at a glance from the share.
 *
 * Every number comes from the data. Nothing is rounded into a claim it cannot support: the share
 * is the stored fraction, the count of other voices is the length of the rest of the array.
 */
function SpeakerNote({ speakers }: { speakers: TranscriptSpeaker[] }) {
  const professor = speakers.find((speaker) => speaker.role === 'professor')
  const others = speakers.filter((speaker) => speaker.role !== 'professor').length
  if (!professor) return null
  return (
    <p className="flex-none text-[11px] font-normal leading-[1.35] text-microcopy text-pretty">
      {`Professor (likely) spoke ${Math.round(professor.share * 100)}% · `}
      {others === 0 ? 'no other voices' : others === 1 ? '1 other voice' : `${others} other voices`}
      {' · decided by talk time'}
    </p>
  )
}

function SegmentRow({
  segment,
  needle,
  active,
  showSpeaker,
}: {
  segment: TranscriptSegment
  needle: string
  active: boolean
  /** True on the first line of a run by one voice. See the map that renders this. */
  showSpeaker: boolean
}) {
  return (
    <div
      className={[
        'flex gap-3 rounded-button px-[10px] py-2',
        active ? 'bg-warning-fill' : 'hover:bg-surface-2',
      ].join(' ')}
    >
      <button
        type="button"
        disabled
        title="There is nothing here to play from: the recording is archived on your desktop PC and no route hands it back. The timestamp itself is real — it is what a citation points at."
        className="w-[38px] flex-none cursor-not-allowed text-left text-[11px] font-semibold leading-[1.5] text-success-dot"
      >
        {msToTimestamp(segment.startMs)}
      </button>
      <p className="min-w-0 flex-1 text-[12.5px] font-normal leading-[1.6] text-ink-2 text-pretty">
        {showSpeaker && segment.speaker && (
          <span className="font-semibold text-ink">{speakerLabel(segment.speaker)}: </span>
        )}
        <Highlighted text={segment.text} needle={needle} />
      </p>
    </div>
  )
}

/** Matched terms highlighted in place (README §Search: "Highlight matched terms in place"). */
function Highlighted({ text, needle }: { text: string; needle: string }) {
  if (!needle) return <>{text}</>
  const parts: React.ReactNode[] = []
  const lower = text.toLowerCase()
  let cursor = 0
  let found = lower.indexOf(needle, cursor)
  while (found !== -1) {
    if (found > cursor) parts.push(text.slice(cursor, found))
    parts.push(
      <mark
        key={`${found}-${parts.length}`}
        className="rounded-[3px] bg-highlight px-[2px] text-ink-2"
      >
        {text.slice(found, found + needle.length)}
      </mark>,
    )
    cursor = found + needle.length
    found = lower.indexOf(needle, cursor)
  }
  if (cursor < text.length) parts.push(text.slice(cursor))
  return <>{parts}</>
}
